import { useCustom } from "@refinedev/core";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { PlacementTable } from "@/components/PlacementTable";
import { query } from "@/lib/api";
import {
  boxWord,
  errorDisplay,
  errorMessage,
  filterRows,
  perDay,
  productWord,
  uniqueBoxCount,
  windowFrom,
} from "@/lib/format";
import type { Locations, Report } from "@/lib/providers";
import { useSessionGuard } from "@/lib/useSessionGuard";

const PRESETS = [7, 14, 30];
const DEFAULT_DAYS = 30;
const DEFAULT_CAPACITY = 40;

const FIELD =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px] focus:border-border-strong";

/**
 * Отчёт о размещении коробов.
 *
 * Параметры РАСЧЁТА живут в адресе (город, период, ёмкость): ссылку на
 * посчитанный отчёт можно переслать сменщику, и он увидит ровно то же.
 * Фильтры по модели и карточке в адрес не уезжают намеренно — это линза
 * поверх уже посчитанных строк, а не параметр запроса, и подмешивать их к
 * параметрам расчёта значило бы обещать пересчёт, которого не будет.
 */
export function PlacementPage() {
  const [params, setParams] = useSearchParams();

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  };

  const preset = windowFrom(DEFAULT_DAYS);
  const dateFrom = params.get("from") ?? preset.from;
  const dateTo = params.get("to") ?? preset.to;
  const capacity = Number(params.get("cap")) || DEFAULT_CAPACITY;

  // Активный пресет вычисляется из самих дат, а не хранится рядом с ними:
  // два источника правды разъехались бы на первой же ссылке извне.
  const activePreset = PRESETS.find(
    (days) =>
      windowFrom(days).from === dateFrom && windowFrom(days).to === dateTo,
  );
  const [showDates, setShowDates] = useState(activePreset === undefined);

  // Города. Список нужен экрану ДО того, как выбран город, поэтому отдельным
  // запросом. Его отказ — не повод оставить сотрудника без отчёта: селектор
  // не появится, отчёт посчитается по городу из настроек сервиса.
  const locations = useCustom<Locations>({
    url: "/api/report/locations",
    method: "get",
  });
  // `result.data` у Refine существует и ДО загрузки — пустым объектом.
  // Поэтому список городов нормализуется здесь один раз: иначе каждое
  // обращение к `known.locations` пришлось бы страховать отдельно, и первое
  // же незастрахованное роняло бы экран (уже роняло — на `.length`).
  const cities = locations.result.data?.locations ?? [];
  const cityId =
    Number(params.get("city")) ||
    locations.result.data?.default_location_id ||
    0;

  const url = useMemo(
    () =>
      `/api/report/placement${query({
        capacity,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: cityId > 0 ? cityId : undefined,
      })}`,
    [capacity, dateFrom, dateTo, cityId],
  );

  // Отчёт ждёт, пока решится список городов. Без этого первая загрузка ушла
  // бы без города, а следом сразу вторая, с городом: два одинаковых запроса
  // на каждое открытие экрана.
  const settled = !locations.query.isPending;
  const reportQuery = useCustom<Report>({
    url,
    method: "get",
    queryOptions: { enabled: settled },
  });

  // Отчётом считается только payload, В КОТОРОМ ЕСТЬ СТРОКИ. `result.data`
  // у Refine существует и до загрузки — пустым объектом, — и проверка на
  // «не null» пропустила бы его дальше как готовый отчёт: экран падал на
  // `report.rows` ещё до первого ответа сервера.
  const loaded = reportQuery.result.data;
  const report = loaded && Array.isArray(loaded.rows) ? loaded : null;
  const busy = reportQuery.query.isFetching;
  const error = reportQuery.query.error
    ? errorMessage(reportQuery.query.error)
    : "";

  // Истёкшая сессия уводит на вход, а не оставляет с сообщением без выхода.
  useSessionGuard(reportQuery.query.error);

  const [filterModel, setFilterModel] = useState("");
  const [filterCard, setFilterCard] = useState("");
  const filtered = !!filterModel.trim() || !!filterCard.trim();
  const shown = report ? filterRows(report.rows, filterModel, filterCard) : [];
  const resetFilters = () => {
    setFilterModel("");
    setFilterCard("");
  };

  // rows.length — число СТРОК (карточка товара внутри короба), а не коробов:
  // в одном коробе бывает несколько товаров. Ёмкость зоны сотрудник задаёт в
  // коробах, поэтому заголовок обязан назвать именно короба — иначе он
  // понесёт в зону больше коробов, чем туда влезает.
  const boxCount = report ? uniqueBoxCount(report.rows) : 0;

  const applyPreset = (days: number) => {
    const w = windowFrom(days);
    setShowDates(false);
    set({ from: w.from, to: w.to });
  };

  // Полноэкранная ошибка — только когда показывать вообще нечего. Если отчёт
  // уже на экране, сбой пересчёта не должен стирать список, который сотрудник
  // читает прямо сейчас с коробом в руках.
  if (error && errorDisplay(report !== null) === "replace") {
    return (
      <main
        className="grid flex-1 place-items-center p-5"
        data-component="PlacementPage"
      >
        <div
          role="alert"
          className="flex max-w-[420px] flex-col items-center gap-3 rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-6 text-center"
        >
          <AlertTriangle size={20} className="text-err" />
          <p className="text-[13.5px]">{error}</p>
          <button
            type="button"
            onClick={() => reportQuery.query.refetch()}
            className="h-8 rounded-[var(--radius-ui)] bg-fg px-3 text-[13px] font-medium text-bg"
          >
            Повторить
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 md:overflow-hidden md:p-5"
      data-component="PlacementPage"
      data-annot="размещение"
    >
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.02em]">
            Короба{" "}
            <span className="font-normal text-fg-muted">
              в зону быстрого доступа
            </span>
          </h1>
          <p className="mt-1 text-[12.5px] text-fg-muted">
            Отчёт советует, какие короба перенести. Ничего не меняет —
            переставляет человек.
          </p>
        </div>

        {/* Ряд переносится по строкам, а не клипается: на экране 430px он не
            влезает, а горизонтальный скролл в проходе между стеллажами
            означает, что до кнопки «Пересчитать» просто не добраться. */}
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            reportQuery.query.refetch();
          }}
        >
          {/* Город — первый: он меняет не параметр отчёта, а сам склад, про
              который отчёт. Селектора нет вовсе, когда городов меньше двух:
              список с единственным вариантом — не выбор, а лишний элемент. */}
          {cities.length > 1 ? (
            <>
              <label className="text-[12.5px] text-fg-muted" htmlFor="city">
                Город
              </label>
              <select
                id="city"
                className={FIELD}
                value={cityId}
                onChange={(e) => set({ city: e.target.value })}
              >
                {cities.map((loc) => (
                  <option key={loc.location_id} value={loc.location_id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </>
          ) : null}

          <div
            className="flex flex-wrap gap-1"
            role="group"
            aria-label="Период"
          >
            {PRESETS.map((days) => (
              <button
                key={days}
                type="button"
                aria-pressed={!showDates && activePreset === days}
                onClick={() => applyPreset(days)}
                className={
                  "h-8 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] transition-colors " +
                  (!showDates && activePreset === days
                    ? "border-border-strong bg-bg-hover font-medium text-fg"
                    : "border-border text-fg-muted hover:bg-bg-hover hover:text-fg")
                }
              >
                {days} дней
              </button>
            ))}
            <button
              type="button"
              aria-pressed={showDates}
              onClick={() => setShowDates((v) => !v)}
              className={
                "h-8 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] transition-colors " +
                (showDates
                  ? "border-border-strong bg-bg-hover font-medium text-fg"
                  : "border-border text-fg-muted hover:bg-bg-hover hover:text-fg")
              }
            >
              Свой период
            </button>
          </div>

          {showDates ? (
            <>
              <label className="sr-only" htmlFor="date-from">
                С
              </label>
              <input
                id="date-from"
                type="date"
                className={FIELD}
                value={dateFrom}
                max={dateTo}
                onChange={(e) => set({ from: e.target.value })}
              />
              <label className="sr-only" htmlFor="date-to">
                По
              </label>
              <input
                id="date-to"
                type="date"
                className={FIELD}
                value={dateTo}
                min={dateFrom}
                onChange={(e) => set({ to: e.target.value })}
              />
            </>
          ) : null}

          <label className="text-[12.5px] text-fg-muted" htmlFor="capacity">
            Мест в зоне
          </label>
          <input
            id="capacity"
            type="number"
            min={1}
            max={500}
            className={FIELD + " w-[72px] tnum"}
            value={capacity}
            onChange={(e) => set({ cap: e.target.value })}
          />

          <button
            type="submit"
            disabled={busy}
            className="flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] bg-fg px-3 text-[13px] font-medium text-bg disabled:opacity-60"
          >
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
            {busy ? "Считаю…" : "Пересчитать"}
          </button>
        </form>
      </div>

      {/* Отчёт уже на экране — сбой пересчёта показывается рядом со списком, а
          не вместо него: старые данные всё ещё верны. */}
      {error ? (
        <Banner tone="err" action={() => reportQuery.query.refetch()}>
          {error}
        </Banner>
      ) : null}

      {report && !report.ozon.available ? (
        // Треть спроса приходит с Ozon. Показать отчёт без него как полный
        // значило бы соврать в главном числе экрана.
        <Banner tone="warn">
          Спрос Ozon недоступен — в отчёте только Wildberries
        </Banner>
      ) : report && report.date_from < report.ozon.since ? (
        // Строки сравниваются как строки намеренно: обе в YYYY-MM-DD, где
        // лексикографический порядок совпадает с хронологическим.
        <Banner tone="warn">
          Данные Ozon есть только с {report.ozon.since} — начало периода
          посчитано по одному Wildberries
        </Banner>
      ) : null}

      {!report ? (
        <p className="text-[13px] text-fg-muted">Считаю…</p>
      ) : (
        <>
          {/* Сводка — отдельными плитками, а не фразой: это четыре разных
              числа с разными знаменателями, и в одном предложении они
              склеивались в утверждение, которого никто не делал. */}
          <dl
            className="mb-4 grid grid-cols-2 gap-2 lg:grid-cols-4"
            aria-busy={busy}
          >
            <Metric
              label="Перенести"
              value={boxCount}
              unit={boxWord(boxCount)}
              note={`из ${report.total_boxes} занятых`}
            />
            <Metric
              label="Закроют отборов"
              value={report.coverage_pct}
              unit="%"
              accent
              // Знаменатель — ВЕСЬ спрос периода, включая товары ниже порога.
              // Считать процент по одним прошедшим порог значило бы поднять
              // метрику, ничего не сделав со складом.
              note={
                `за ${report.days} дней` +
                (report.cards_below_threshold > 0
                  ? ` · ниже порога ${report.threshold_per_day}/день — ${report.cards_below_threshold} товаров`
                  : "")
              }
            />
            <Metric
              label="Товаров в них"
              value={report.rows.length}
              unit={productWord(report.rows.length)}
              note="строк отчёта"
            />
            <Metric
              label="Всего отборов"
              value={perDay(report.picks_per_day)}
              unit="/день"
              // База расчёта названа поимённо: цифра сложена из двух площадок,
              // и если Ozon отвалится, разница будет видна здесь.
              note={`${report.total_picks} за ${report.days} дн · ВБ ${report.picks_wb} · Озон ${report.picks_ozon}`}
            />
          </dl>

          {report.rows.length === 0 ? (
            // report есть, строк нет — это не «идёт загрузка», а честный
            // пустой результат: при такой ёмкости зоны переносить нечего.
            <Empty>Нет коробов, которые стоит переносить.</Empty>
          ) : (
            <>
              {/* Фильтр отделён от формы вверху: там параметры расчёта, здесь
                  линза поверх готовых строк. Ни ранга, ни покрытия он не
                  трогает. */}
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1.5">
                  <label
                    className="text-[12.5px] text-fg-muted"
                    htmlFor="f-model"
                  >
                    Модель
                  </label>
                  <input
                    id="f-model"
                    type="search"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="номер"
                    className={FIELD + " w-[110px] tnum"}
                    value={filterModel}
                    onChange={(e) => setFilterModel(e.target.value)}
                  />
                </span>
                <span className="flex items-center gap-1.5">
                  <label
                    className="text-[12.5px] text-fg-muted"
                    htmlFor="f-card"
                  >
                    Карточка
                  </label>
                  <input
                    id="f-card"
                    type="search"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="номер"
                    className={FIELD + " w-[110px] tnum"}
                    value={filterCard}
                    onChange={(e) => setFilterCard(e.target.value)}
                  />
                </span>
                {filtered ? (
                  <>
                    {/* Сколько строк спрятано — не украшение: без этого числа
                        сотрудник не отличит «такого товара в зоне нет» от
                        «фильтр набран с опечаткой». */}
                    <span role="status" className="text-[12.5px] text-fg-muted">
                      показано {shown.length} из {report.rows.length}
                    </span>
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="h-8 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12.5px] text-fg-muted hover:bg-bg-hover hover:text-fg"
                    >
                      Сбросить
                    </button>
                  </>
                ) : null}
              </div>

              {shown.length === 0 ? (
                <Empty>
                  Под фильтр не попала ни одна строка.
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-3 block h-8 rounded-[var(--radius-ui)] bg-fg px-3 text-[13px] font-medium text-bg"
                  >
                    Сбросить фильтр
                  </button>
                </Empty>
              ) : (
                <PlacementTable rows={shown} days={report.days} />
              )}
            </>
          )}
        </>
      )}
    </main>
  );
}

/**
 * Плитка сводки. Единица измерения отдельным элементом и мельче числа:
 * «40 коробов» одним кеглем читается как одно длинное число.
 */
function Metric({
  label,
  value,
  unit,
  note,
  accent,
}: {
  label: string;
  value: string | number;
  unit: string;
  note: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3">
      <dt className="text-[11.5px] uppercase tracking-[0.04em] text-fg-muted">
        {label}
      </dt>
      <dd
        className={
          "mt-1 text-[24px] font-semibold tabular-nums tracking-[-0.02em] " +
          (accent ? "text-accent-text" : "")
        }
      >
        {value}
        <span className="ml-1 text-[13px] font-normal text-fg-muted">
          {unit}
        </span>
      </dd>
      <p className="mt-1 text-[11.5px] text-fg-muted">{note}</p>
    </div>
  );
}

/**
 * Полоса-сообщение над отчётом. Цветом здесь красится значок и рамка, а
 * текст остаётся основным: цветной текст на цветной подложке в тёмной теме
 * даёт 4.41:1 — ниже 4.5:1, которые WCAG требует от обычного текста
 * (замерено src/lib/contrast.ts, порог держит src/lib/theme.test.ts).
 */
function Banner({
  tone,
  children,
  action,
}: {
  tone: "err" | "warn";
  children: React.ReactNode;
  action?: () => void;
}) {
  return (
    <div
      role={tone === "err" ? "alert" : "status"}
      className={
        "mb-3 flex items-center gap-2 rounded-[var(--radius-ui)] border px-3 py-2 text-[12.5px] " +
        (tone === "err"
          ? "border-err/60 bg-err-bg"
          : "border-warn/60 bg-warn-bg")
      }
    >
      <AlertTriangle
        size={14}
        className={tone === "err" ? "text-err" : "text-warn"}
      />
      <span>{children}</span>
      {action ? (
        <button
          type="button"
          onClick={action}
          className="ml-auto rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12px] hover:bg-bg-hover"
        >
          Повторить
        </button>
      ) : null}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
      {children}
    </div>
  );
}
